import { Container, autoinject, transient } from "aurelia-dependency-injection";
import { bindable, View, ViewEngine } from "aurelia-templating";
import { EventAggregator } from "aurelia-event-aggregator";
import { Router } from "aurelia-router";
import { computedFrom, type Disposable, PLATFORM } from "aurelia-framework";
import type { EntityFinderConfig, EntityFinderResponse, EntityTypeConfigurationModel } from "./comp-entity-finder.models";
import { NotifyPropertyChangedBase } from "../../helpers/reflection";
import { NotificationClientService, NotificationType } from "../../services/basic/notification-client.service";
import { EntityTypeConfigurationHelper } from "../../helpers/entity-type-configuration.helper";
import { Messages } from "../../constants/event-messages.constants";
import { QueriesClientService } from "../../services/queries/queries-client.services";
import * as Enumerable from "linq";
import { ConfigService } from "../../services/basic/config.service";
import * as DOMPurify from "dompurify";

//Load styles
require("./comp-entity-finder.scss");
require("./comp-entity-finder-dropdown.scss");

@autoinject
@transient()
@bindable({
    name:'_filter',
    attribute:'filter'
  })
export class CompEntityFinder extends NotifyPropertyChangedBase implements EntityFinderConfig {

    private _setPromiseCount: number = -9999;
    private _subscriptions: Disposable[] = [];
    private _debounced: any;

    public dropdown: View;
    public results: any = {};
    public isDropdownOpen: boolean;
    public input: HTMLInputElement;
    public currentSelection: any = null;
    public items: EntityFinderItem[] = [];
    public currentItem: EntityFinderItem = null;
    public lengthBeforeSelection: number = 0;
    public menuResults: any[] = [];
    public loading: boolean;
    public focused: boolean;
    public itemsSelected: EntityFinderItem[] = [];
    public itemIsSelected: boolean = false;

    public get noResults(): boolean {
        const keys = this.keys;
        const keysLength = !keys || keys.length == 0 || Enumerable.from(keys).all(x => !this.results[x] || !this.results[x].value || this.results[x].value.length == 0);
        return this.menuResults.length == 0 && keysLength;
    }

    @computedFrom('changes["keys"]')
    get keys() {
        return Object.keys(this.results);
    }

    @computedFrom('changes["keys"]')
    get keysOrdered() {
        const keysOrdered = Enumerable.from(Object.keys(this.results)).orderBy(c => this.supportedEntityTypes.findIndex(x => c == x.uri)).toArray();
        return keysOrdered;
    }

    @bindable
    public theme: string = "primary";

    @bindable
    public triggerOnBlur: boolean;

    @bindable
    public autoSelectResult: boolean = true;

    @bindable
    public placeholder: string = "";

    @bindable
    public showOnAdorner: boolean;

    @bindable
    public showClose: boolean = false;

    @bindable
    public showSearchIcon: boolean = true;

    @bindable
    public iconPosition: IconPosition = 'right';

    @bindable
    public allowSelection: boolean;

    @bindable
    public label: string;

    @bindable
    public selectedEntity: any = null;

    @bindable
    public selectedEntityId: number[] | number = null;

    @bindable
    public selectedEntityUri: string;

    @bindable
    public useInsideHref: boolean = false;

    @bindable
    public baseUrl;

    _filter:string = null; 

    @computedFrom("baseUrl", "configService.config.urls.rightShipPlatformApiUrl")
    get computedBaseUrl(): string {
        return this.baseUrl ?? this.configService.config.urls.rightShipPlatformApiUrl;
    }

    @computedFrom("_filter")
    get filter(): string {
        return this._filter;
    }

    set filter(x:string){
        this._filter = x;
        this.filterValueChanged(x);
    }

    //The text written in the 'input' field
    public filterText: string = null;

    @bindable
    /* Used to filter out entities queried from database */
    public bag: string;

    @bindable
    public info: string = "";

    @bindable
    public infoBottom: string = "";

    @bindable
    public format: string;

    @bindable
    public params: string;

    @bindable
    public displayMemberPath: string;

    @bindable
    public supportedEntityTypes: EntityTypeConfigurationModel[] = [];

    @bindable
    public disabled: boolean = false;

    @bindable
    public readonly: boolean;

    @bindable
    public checkForPermission: boolean = true;

    @bindable
    public hasCreateNew: boolean = false;

    @bindable
    public createNewText: string;

    @bindable
    public tab: number;

    @bindable
    public minSearchLength: number = 1;

    @bindable
    public maxSearchLength: number;

    //added to inject more items from third party libraries like google places
    @bindable
    public onResultsChanged: (x: any) => Promise<any>;

    @bindable
    public clearFilterOnSearch: boolean = false;

    @bindable
    public showViewMore: boolean = false;

    @bindable
    debounceTime: number = 500;

    @bindable
    openDropDownOnClick: boolean = false;

    @bindable
    multiSelect: boolean = false;

    @bindable    
    public footerText: string;

    @bindable
    public footerBindingContext: any;

    constructor(private element: Element,
        private router: Router, private eventAggregator: EventAggregator, private notificationClientService: NotificationClientService,
        private queryService: QueriesClientService, private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        private configService: ConfigService, private viewEngine: ViewEngine) {
        super();
    }

    attached() {
        this._subscriptions.forEach(sub => sub.dispose());
        this._subscriptions = [];
        this._subscriptions.push(this.eventAggregator.subscribe(Messages.documentClick, this.closePopup));

        //Entity finder dropdown
        this.viewEngine.loadViewFactory(PLATFORM.moduleName('@rightship-tech/common-ui/components/comp-entity-finder/comp-entity-finder-dropdown.html')).then(response => {
            this.dropdown = response.create(Container.instance);
        });
    }

    //Clear filter
    removeFilter() {
        this.filter = "";
        this.filterText = "";
        this.selectEntity(null, null, null, true, false, false);
        this.closePopup();
    }

    createNewClick() {
        const paramsEvent: any = { detail: this.filter, bubbles: true };
        const changeTextEvent: CustomEvent = new CustomEvent("create-new-click", paramsEvent);
        this.element.dispatchEvent(changeTextEvent);
        this.closePopup();
    }

    stopPropagation(e, openDropDown: boolean = false) {

        //If there is text, open drop down...  if input is clicked
        if (this.filterText && this.openDropDownOnClick && openDropDown) {
            this.isDropdownOpen = true;
        }

        e.stopPropagation();
    }

    //Close component pop-up
    closePopup = (): void => {
            // require("" + "../../components/entity-finder/entity-finder");
        this.isDropdownOpen = false;
    }

    set(x: { entityTypeUri: string, id: (number | number[]) }): void {
        if (x.id != this.selectedEntityId || x.entityTypeUri != this.selectedEntityUri || !this.selectedEntity) {
            this._setPromiseCount++;
            const localSetPromiseCount = this._setPromiseCount;
            const ids = x.id instanceof Array ? x.id : [x.id];
            const promises = [];
            ids.forEach(singleId => {
                const promise = this.get({
                    entityTypeUri: x.entityTypeUri,
                    id: singleId
                });
                promises.push(promise);
            });
            Promise.all(promises).then(results => {
                if (localSetPromiseCount != this._setPromiseCount) {
                    return;
                }

                this.selectedEntityUri = x.entityTypeUri;
                results.forEach(a => {
                    this.selectedEntity = a;
                    this.handleItemForMultiSelect(a, x.entityTypeUri)
                    if (!(this.clearFilterOnSearch && (this.filter == null || this.filter.trim() == ""))) {
                        if (this.multiSelect && this.itemsSelected.length > 1) {
                            this._filter = this.filterText = "Multiple items selected";
                        }
                        else {
                            this._filter =
                                (this.multiSelect && this.itemsSelected.length == 0) ? "" :
                                    this.queryService.odataModelToString(this.filter, this.format, this.params, 
                                        this.supportedEntityTypes.filter(x => x.uri == this.selectedEntityUri)[0], this.selectedEntity, true);
                        }
                    }
                });
            });

        }
    }

    selectedEntityChanged(newValue, oldValue) {
        if (newValue == oldValue) return;

        if (newValue == null) {
            //https://rightship.atlassian.net/browse/PX-360
            //this.filter = null;
            this.selectedEntityId = null;
        } else {
            if (!(this.clearFilterOnSearch && (this.filter == null || this.filter.trim() == ""))) {
                if (this.multiSelect && this.itemsSelected.length > 1) {
                    this._filter = this.filterText = "Multiple items selected";
                }
                else {
                    this._filter =
                        (this.multiSelect && this.itemsSelected.length == 0) ? "" :
                            this.queryService.odataModelToString(this.filter, this.format, this.params, this.supportedEntityTypes.filter(x => x.uri == this.selectedEntityUri)[0], this.selectedEntity, true);
                }
            }
            if (this.multiSelect) {
                this.selectedEntityId = this.itemsSelected.map(x => x.value.id);
            } else {
                this.selectedEntityId = newValue.id ? Number(newValue.id) : null;
            }
        }
    }

    selectedEntityIdChanged(newValue) {
        if (!newValue || !this.selectedEntityUri) return;
        if (!this.selectedEntity)
            this.set({ entityTypeUri: this.selectedEntityUri, id: this.selectedEntityId });
    }

    selectedEntityUriChanged(newValue) {
        if (!newValue || !this.selectedEntityId) return;
        this.set({ entityTypeUri: this.selectedEntityUri, id: this.selectedEntityId });
    }

    bagChanged() {
        if (this.selectedEntityUri && this.selectedEntityId) {
            this.get({ entityTypeUri: this.selectedEntityUri, id: this.selectedEntityId }).then(a => {
                this.selectedEntityUri = a ? this.selectedEntityUri : null;
                this.selectedEntity = a;
                if (!(this.clearFilterOnSearch && (this.filter == null || this.filter.trim() == ""))) {
                    if (this.multiSelect && this.itemsSelected.length > 1) {
                        this._filter = this.filterText = "Multiple items selected";
                    }
                    else {
                        this._filter =
                            (this.multiSelect && this.itemsSelected.length == 0) ? "" :
                                this.queryService.odataModelToString(this.filter, this.format, this.params, this.supportedEntityTypes.filter(x => x.uri == this.selectedEntityUri)[0], this.selectedEntity, true);
                    }

                    this.closePopup();
                }
            });
        }
    }

    selectEntity($event, entity: any, entityTypeUri: string, hideDropdown: boolean = true, looseFocus: boolean = false, clearFilter: boolean = true): void {
        if (this.allowSelection) {
            this.currentItem = { entityTypeUri: entityTypeUri, value: entity };
            this.currentSelection = entity;
            this.selectedEntity = entity ? entity : null;
            this.selectedEntityUri = entityTypeUri;
            const asString = this.queryService.odataModelToString(this.filter, this.format, this.params, this.supportedEntityTypes.filter(x => x.uri == this.selectedEntityUri)[0], this.selectedEntity, true);

            if (hideDropdown) {
                this.isDropdownOpen = false;
            }

            if ($event) {
                $event.stopPropagation();
            }

            this.handleItemForMultiSelect(entity, entityTypeUri);

            setTimeout(() => {
                if (looseFocus)
                    this.input.blur();

                if (clearFilter) {
                    if (!this.clearFilterOnSearch) {
                        //If multiple filters can and are selected
                        if (this.multiSelect && this.itemsSelected.length > 1) {
                            this._filter = this.filterText = "Multiple items selected";
                        }
                        //If a single filter is selected
                        else {
                            this._filter = this.filterText = (this.multiSelect && this.itemsSelected.length == 0) ? "" :
                                asString?.indexOf("undefined") == -1 ? asString : "";
                        }
                    } else {
                        this.filter = "";
                        this.filterText = null;
                    }
                }
            });
            
            if (this.currentSelection) {
                this.itemIsSelected =  true;
            }
            
            //Raise 'item-selected' event
            this.element.dispatchEvent(
                new CustomEvent("item-selected",
                    {
                        bubbles: true,
                        detail: !this.multiSelect ? { selectedEntity: this.selectedEntity, selectedEntityUri: this.selectedEntityUri } : this.itemsSelected
                    })
            );
        }
    }

    private handleItemForMultiSelect(entity: any, entityTypeUri: string) {
        if (this.multiSelect && entity) {
            const currentIndex = this.itemsSelected.findIndex(x => x.value.id == entity.id && x.entityTypeUri == entityTypeUri);

            if (currentIndex == -1) {
                this.itemsSelected.push(<EntityFinderItem>{ entityTypeUri: entityTypeUri, value: entity });
            }
            else {
                this.itemsSelected.splice(currentIndex, 1);
            }
        }
    }

    viewMoreClick(uri: string) {
        this.closePopup();
        this.element.dispatchEvent(
            new CustomEvent("view-more-click",
                {
                    bubbles: true,
                    detail: { filter: this.filter, uri: uri }
                })
        );
    }

    selectMenuItem(menuItem: any) {
        this.isDropdownOpen = false;

        setTimeout(() => {
            this.input.blur();
            this.filter = "";
        });

        this.router.navigateToRoute(menuItem.route);
    }

    setFirst(filter: string, supportedEntityTypes: EntityTypeConfigurationModel[]): Promise<void> {
        this.filter = filter;
        return new Promise<void>((resolve) => {
            this.search(supportedEntityTypes).then(x => {
                this.items = Enumerable.from<EntityFinderResponse>(x).orderBy(c => this.supportedEntityTypes.findIndex(x => c.entityTypeUri == x.uri))
                    .selectMany<EntityFinderItem>(x => {
                        const item: EntityFinderItem[] = Enumerable.from(x.response["value"]).select(
                            a => <EntityFinderItem>{
                                entityTypeUri: x.entityTypeUri,
                                value: a
                            }).toArray();

                        return item;
                    }).toArray();
                const filtered = Enumerable.from<EntityFinderItem>(this.items);
                this.currentItem = filtered.firstOrDefault();
                if (this.currentItem)
                    this.selectEntity(null, this.currentItem.value, this.currentItem.entityTypeUri, true, true);
                resolve();
            });
        });
    }


    setFirstItem(filter: string, supportedEntityTypes: EntityTypeConfigurationModel[]): Promise<void> {
        this.filter = filter;
        return new Promise<void>((resolve) => {
            this.search(supportedEntityTypes).then(x => {
                this.items = Enumerable.from<EntityFinderResponse>(x).orderBy(x => this.supportedEntityTypes.findIndex(y => y.uri == x.entityTypeUri))
                    .selectMany<EntityFinderItem>(x => {
                        const item: EntityFinderItem[] = Enumerable.from(x.response["value"]).select(
                            a => <EntityFinderItem>{
                                entityTypeUri: x.entityTypeUri,
                                value: a
                            }).toArray();

                        return item;
                    }).toArray();
                const filtered = Enumerable.from<EntityFinderItem>(this.items);
                this.currentItem = filtered.firstOrDefault(x => x.value == filter);
                this.selectEntity(null, this.currentItem.value, this.currentItem.entityTypeUri, true, true);
                resolve();
            });
        });
    }

    static any(queryService: QueriesClientService, filter: string, bag: string, supportedEntityTypes: EntityTypeConfigurationModel[]): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            const promises: Promise<any>[] = [];
            const types = supportedEntityTypes.filter(x => x.uri != null);
            types.forEach((type: EntityTypeConfigurationModel) => {
                const promise = new Promise<any>((resolve) => {
                    const queryString = type.uri.split("?");
                    const param1 = encodeURIComponent(CompEntityFinder.regexReplace(filter));
                    const param2 = bag;
                    const uri = "/odata/" + (queryString[0].replace(/\s/g, "")) + "/Genesis.EntityFinderWithBag(filter=@p1,bag=@p2)?@p1='" + param1 + "'&@p2='" + param2 + "'" + (queryString[1] ? `&${queryString[1]}` : "");
                    queryService.getData(uri)
                        .then((response: any[]) => {
                            resolve({ entityTypeUri: type.uri, response: response });
                        });
                });

                promises.push(promise);
            });

            const all = Promise.all(promises);
            all.then((results: any[]) => {
                const items = Enumerable.from<EntityFinderResponse>(results).orderBy(x => supportedEntityTypes.findIndex(y => y.uri == x.entityTypeUri))
                    .selectMany<EntityFinderItem>(x => {
                        const item: EntityFinderItem[] = Enumerable.from(x.response["value"]).select(
                            a => <EntityFinderItem>{
                                entityTypeUri: x.entityTypeUri,
                                value: a
                            }).toArray();

                        return item;
                    }).toArray();
                const filtered = Enumerable.from<EntityFinderItem>(items);
                resolve(filtered.any());
            });
        });
    }

    get(selectedEntity: any): Promise<any> {
        const promise = new Promise<any>((resolve) => {
            const entity = this.entityTypeConfigurationHelper.getConfiguration([selectedEntity.entityTypeUri], this.supportedEntityTypes)[0];
            const entityWithPerm = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions([selectedEntity.entityTypeUri], this.supportedEntityTypes)[0];
            const typeFound = this.checkForPermission && entity ? entity.uri : (entityWithPerm ? entityWithPerm.uri : null);
            if (typeFound && selectedEntity && selectedEntity.id) {
                const queryString = typeFound.split("?");
                const uri = this.computedBaseUrl + "/odata/" + (queryString[0].replace(/\s/g, "")) +
                    "/Genesis.FindIdWithBag(filter=@p1,bag=@p2)?" + "@p1='" + selectedEntity.id + "'&@p2='" + this.bag + "'" + (queryString[1] ? `&${queryString[1]}` : "");
                this.queryService.getData(uri)
                    .then((response: any) => {
                        const firstOrDefault = Enumerable.from(<any[]>response.value)
                            .firstOrDefault(a => {
                                const id: any = a.id;
                                return id && id == selectedEntity.id;
                            });

                        resolve(firstOrDefault ? firstOrDefault : null);
                    });
            } else {
                resolve(null);
            }
        });
        return promise;
    }

    static regexReplace(str: string) {
        return str.replace(/'/g, "''");
    }

    search(supportedEntityTypes: EntityTypeConfigurationModel[] = null): Promise<EntityFinderResponse[]> {
        const promises: Promise<any>[] = [];
        this.loading = true;
        const types = supportedEntityTypes ? supportedEntityTypes : this.supportedEntityTypes;
        for (const x of types.filter(x => x.uri != null)) {
            const promise = new Promise<any>((resolve) => {
                const queryString = x.uri.split("?");
                const param1 = encodeURIComponent(CompEntityFinder.regexReplace(this.filterText));
                const param2 = this.bag;
                const uri = this.computedBaseUrl + "/odata/" + queryString[0].replace(/\s/g, "") + "/Genesis.EntityFinderWithBag(filter=@p1,bag=@p2)?" + "@p1='" + param1 + "'&@p2='" + param2 + "'" + (queryString[1] ? `&${queryString[1]}` : "");
                this.queryService.getData(uri)
                    .then((response: any) => {
                        const item = { entityTypeUri: x.uri, response: response };

                        if (item.response && item.response.value) {
                            this.results[item.entityTypeUri] = item.response;
                        }
                        else {
                            delete this.results[item.entityTypeUri];
                        }

                        if (this.onResultsChanged) {
                            this.onResultsChanged(this.results[item.entityTypeUri]).then(a => {
                                this.results[item.entityTypeUri] = a;
                            }, () => {
                            });
                        }

                        this.onPropertyChanged<CompEntityFinder>(z => z.keys);

                        resolve(item);
                    }, () => {
                        const item = { entityTypeUri: x.uri };
                        delete this.results[item.entityTypeUri];
                        this.onPropertyChanged<CompEntityFinder>(z => z.keys);
                    });
            });

            promises.push(promise);
        }
        const all = Promise.all(promises);
        all.then(() => {
            this.loading = false;
        }, () => {
            this.loading = false;
        });
        return all;
    }

    public asString(uri: string, value, multiSelect: boolean = false): string {
        
        if (!value) return null;
        
        const config = Enumerable.from(this.supportedEntityTypes).firstOrDefault(x => x.uri == uri);
        value.multiSelect = multiSelect;
        return this.queryService.odataModelToString(this.filter, this.format, this.params, config, value, false);
    }

    keyDown(e) {
        if (this.itemIsSelected) {
            this.filter = "";
            this.filterText = null;
            this.itemIsSelected = false;
        }
        let index = Enumerable.from(this.items).select(x => JSON.stringify(x)).toArray()
            .indexOf(JSON.stringify(this.currentItem));

        if (e.keyCode === 40) // down
        {
            index = Math.min(index + 1, this.items.length - 1);
        } else if (e.keyCode === 38) // up
        {
            index = Math.max(index - 1, 0);
        } else if (e.keyCode === 13) // enter
        {
            if (this.currentItem) {
                this.selectEntity(null, this.currentItem.value, this.currentItem.entityTypeUri, true, true);
            }
            return false;
        }

        if (e.keyCode == 40 || e.keyCode == 38) {
            this.currentItem = this.items[index];
            setTimeout(() => {
                const scrollViewerJQuery = $(".entity-finder-dropdown.visible > .entity-finder-dropdown-list");
                const scrollViewer = scrollViewerJQuery.get(0);
                const ele = $(this.scrollForm).find(".selected")[0] ? $(this.scrollForm).find(".selected")[0] : scrollViewerJQuery.find(".selected")[0];

                if (ele) {
                    const eleRect = ele.getBoundingClientRect();
                    const scrollViewerRect = scrollViewer.getBoundingClientRect();

                    if (!(eleRect.bottom <= scrollViewerRect.bottom)) {
                        scrollViewer.scrollTop += (eleRect.bottom - scrollViewerRect.bottom);
                    }
                    if (!(eleRect.top >= scrollViewerRect.top)) {
                        scrollViewer.scrollTop += (eleRect.top - scrollViewerRect.top);
                    }
                }
            });
            e.stopPropagation();
            e.preventDefault();
            return false;
        }

        return true;
    }


    scrollForm: any;

    mouseover(index: number, uri: string) {
        this.currentMenuRoute = null;
        this.currentItem = Enumerable.from(this.items).where(x => x.entityTypeUri == uri).toArray()[index];
    }

    public currentMenuRoute: any;

    mouseoverMenu(index: number) {
        this.currentItem = null;
        this.currentMenuRoute = this.menuResults[index];
    }

    formatChanged() {
        if (!(this.clearFilterOnSearch && (this.filter == null || this.filter.trim() == ""))) {
            if (this.multiSelect && this.itemsSelected.length > 1) {
                this._filter = this.filterText = "Multiple items selected";
            }
            else {
                this._filter =
                    (this.multiSelect && this.itemsSelected.length == 0) ? "" :
                        this.queryService.odataModelToString(this.filter, this.format, this.params, this.supportedEntityTypes.filter(x => x.uri == this.selectedEntityUri)[0], this.selectedEntity, true);
            }
        }
    }

    filterValueChanged(newValue: string, force: boolean = false): void {
        if (this.input || force) {
            if (this.selectedEntity && !force && this.filter) {
                const currentFilterValue = this.queryService.odataModelToString(this.filter, this.format, this.params, this.supportedEntityTypes.filter(x => x.uri == this.selectedEntityUri)[0], this.selectedEntity, true);
                if (currentFilterValue == newValue) {
                    return;
                }
            }

            if (this._debounced) {
                clearTimeout(this._debounced);
            }

            this._debounced = setTimeout(
                () => {

                    if (this.triggerOnBlur) {
                        this.selectedEntity = null;
                        this.selectedEntityUri = null;
                    }
                    if (!this.results) {
                        this.results = {};
                    }

                    if (this.supportedEntityTypes) {
                        this.supportedEntityTypes.forEach(x => {
                            if (!this.results[x.uri]) {
                                this.results[x.uri] = [];
                            }
                        });
                    }

                    if (this.filterText && this.filterText.length >= this.minSearchLength && (!this.maxSearchLength || this.filterText.length <= this.maxSearchLength)) {
                        this.isDropdownOpen = true;
                        this.search().then(x => {
                            this.items = Enumerable.from<EntityFinderResponse>(x).orderBy(x => this.supportedEntityTypes.findIndex(y => y.uri == x.entityTypeUri))
                                .selectMany<EntityFinderItem>(x => {
                                    const item: EntityFinderItem[] = Enumerable.from(x.response["value"]).select(
                                        a => <EntityFinderItem>{
                                            entityTypeUri: x.entityTypeUri,
                                            value: a
                                        }).toArray();

                                    return item;
                                }).toArray();
                            const filtered = Enumerable.from<EntityFinderItem>(this.items);
                            // if there is only one result select it
                            if (this.autoSelectResult) {
                                this.currentItem = filtered.firstOrDefault();
                            }
                        });
                    } else {
                        if (this.filterText && this.maxSearchLength && this.filterText.length > this.maxSearchLength) {
                            this.notificationClientService.show("Error", "Text entered in the search is too long.", NotificationType.Error);
                        }
                        this.items = [];

                        this.onPropertyChanged<CompEntityFinder>(z => z.keys);

                        this.selectEntity(null, null, null, true, false, false);
                    }
                }, this.debounceTime);
        }
    }

    entityTypeName(uri: string): string {
        const entityTypeName = Enumerable.from(this.supportedEntityTypes).firstOrDefault(y => y.uri == uri);
        return entityTypeName.title;
    }

    onContentPasted($event) {

        $event.stopPropagation();
        let text = '';
        if (typeof $event.clipboardData === 'undefined') {
            text = window["clipboardData"].getData('Text');
        }
        else {
            text = $event.clipboardData.getData('text/plain');
        }

        const windowSelection = window.getSelection();
        if (windowSelection.rangeCount > 0) {

            if (text) {
                text = text.trim();
                text = text.replace(/(\r\n|\n|\r)/gm, "");
            }

            const range = windowSelection.getRangeAt(0).cloneRange();
            range.deleteContents();
            setTimeout(() => {
                const textNode = document.createTextNode(text);
                range.insertNode(textNode);

                // Place the caret immediately after the text added
                range.setStartAfter(textNode);
                range.collapse(true);
                windowSelection.removeAllRanges();
                windowSelection.addRange(range);

                this.input.focus();
            });
        }
    }

    static encodedFilter(filter: string) {
        const div = document.createElement('div');
        div.innerHTML = DOMPurify.sanitize(filter);
        const text = div.textContent;
        div.remove();
        return text;
    }

    supportedEntityTypesChanged() {
        if (this.supportedEntityTypes && this.supportedEntityTypes.length == 1) {
            this.selectedEntityUri = this.supportedEntityTypes[0].uri;
        }
    }

    currentContext: CompEntityFinder = this;

    focus() {
        if (!this.disabled) {
            this.focused = true;
        }

        //Check if there are itemsSelected, if yes, open the dropdown so users can see the selections
        if(this.itemsSelected.length > 0) {
            this.isDropdownOpen = true;

            //If multiSelect, filter set it to empty string so that users can search a new item
            if(this.multiSelect) {
                this.filter = "";
            }
        }
    }

    blur() {
        this.focused = false;
        if (this.triggerOnBlur) {
            let entity = this.selectedEntity;
            let entityType = this.selectedEntityUri;
            if (!entity && this.displayMemberPath) {
                entity = {};
                entity[this.displayMemberPath] = CompEntityFinder.encodedFilter(this.filter);
            }
            if (!entityType && this.supportedEntityTypes.length > 0)
                entityType = this.supportedEntityTypes[0].uri;

            this.element.dispatchEvent(
                new CustomEvent("item-selected",
                    {
                        bubbles: true,
                        detail: { selectedEntity: entity, selectedEntityUri: entityType }
                    })
            );
        }
    }

    checkIfShouldRemove(uri: string, value: any, multiSelect: boolean = false) {

        const elementStringValue = this.asString(uri, value, multiSelect)

        if (!elementStringValue) {
            const indexToRemove = this.results[uri].value.findIndex(x => value && x.id == value.id);

            if (indexToRemove > -1) {
                this.results[uri].value.splice(indexToRemove, 1);
            }

            return true;
        }

        return false;
    }

    detached() {
        this._subscriptions.forEach(sub => sub.dispose());
        this._subscriptions = [];
    }

    public getEntityFinderView() {
        return PLATFORM.moduleName('@rightship-tech/common-ui/components/comp-entity-finder/comp-entity-finder-dropdown.html');
    }
}

export interface EntityFinderItem {
    entityTypeUri: string,
    value: any;
}

type IconPosition = 'left' | 'right';